import React, { useState } from 'react';
import { BookOpen, HelpCircle, ChevronDown, ChevronUp } from 'lucide-react';

const signs = [
  { name: 'Hello', desc: 'All 5 fingers open, flat palm facing camera', symbol: '👋' },
  { name: 'Yes', desc: 'Make a fist with thumb pointing up', symbol: '👍' },
  { name: 'No', desc: 'Index and middle fingers extended', symbol: '✌️' },
  { name: 'Love', desc: 'Thumb, index, and pinky extended out', symbol: '🤟' },
  { name: 'Water', desc: 'Index, middle, and ring fingers up', symbol: '💧' },
  { name: 'Help', desc: 'Make a fist (closed hand)', symbol: '✊' },
  { name: 'V / Peace', desc: 'Index and middle fingers up', symbol: '✌️' },
  { name: 'I', desc: 'Only pinky finger extended up', symbol: '🪖' },
  { name: 'L', desc: 'Thumb and index finger in an L shape', symbol: '👆' },
];

const faqs = [
  { q: 'How does the AI detection work?', a: 'SignAI uses Google\'s MediaPipe technology to map 21 3D landmarks on your hand directly in your browser. It calculates the angles and distances between your finger joints to detect gestures in real-time, completely offline.' },
  { q: 'Why is the translation sometimes inaccurate?', a: 'Make sure your hand is clearly visible, well-lit, and ~30-50cm from the camera. The AI relies on seeing all your fingers clearly. Hold the sign steady for 1-2 seconds for best results.' },
  { q: 'Is my video feed private?', a: 'Yes, 100% private. The AI model runs entirely on your local device via WebAssembly. No video data or images are ever transmitted over the internet.' },
  { q: 'How do I save translations?', a: 'As you sign, the translated words build up into a sentence in the Translate tab. You can click "Save to Cloud" to store them securely in Firebase, or "Export" to download a local text file.' },
];

const Instructions = () => {
  const [openFaq, setOpenFaq] = useState(null);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '32px' }}>
      
      {/* Sign Language Guide */}
      <div className="glass-panel">
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '24px' }}>
          <div style={{ background: 'var(--primary-color)', padding: '10px', borderRadius: '12px', color: 'white', display: 'flex' }}>
            <BookOpen size={24} />
          </div>
          <h2 style={{ margin: 0, color: 'var(--primary-color)' }}>Sign Language Guide</h2>
        </div>
        
        <p style={{ color: 'var(--text-muted)', marginBottom: '24px' }}>Learn the signs supported by our AI engine. Hold each sign clearly in front of the camera.</p>
        
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 250px), 1fr))', gap: '16px' }}>
          {signs.map((sign, index) => (
            <div key={index} style={{ 
              background: 'rgba(75, 86, 148, 0.05)', 
              border: '1px solid rgba(75, 86, 148, 0.15)',
              borderRadius: '12px', 
              padding: '16px',
              display: 'flex',
              alignItems: 'center',
              gap: '16px',
              transition: 'transform 0.2s',
              cursor: 'default'
            }}
            onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
            onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
            >
              <div style={{ fontSize: '32px', filter: 'drop-shadow(0 4px 6px rgba(0,0,0,0.1))' }}>
                {sign.symbol}
              </div>
              <div>
                <h4 style={{ margin: '0 0 4px 0', fontSize: '18px', color: 'var(--text-main)' }}>{sign.name}</h4>
                <p style={{ margin: 0, fontSize: '13px', color: 'var(--text-muted)', lineHeight: '1.4' }}>{sign.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* FAQ Section */}
      <div className="glass-panel">
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '24px' }}>
          <div style={{ background: 'rgba(114, 136, 174, 0.2)', padding: '10px', borderRadius: '12px', color: 'var(--primary-color)', display: 'flex' }}>
            <HelpCircle size={24} />
          </div>
          <h2 style={{ margin: 0, color: 'var(--text-main)' }}>Frequently Asked Questions</h2>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {faqs.map((faq, index) => {
            const isOpen = openFaq === index;
            return (
              <div 
                key={index} 
                style={{ 
                  border: '1px solid var(--glass-border)',
                  borderRadius: '12px',
                  overflow: 'hidden',
                  background: isOpen ? 'rgba(75, 86, 148, 0.05)' : 'transparent',
                  transition: 'all 0.3s ease'
                }}
              >
                <button 
                  onClick={() => setOpenFaq(isOpen ? null : index)}
                  style={{ 
                    width: '100%', 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center',
                    padding: '16px 20px',
                    background: 'none',
                    border: 'none',
                    textAlign: 'left',
                    cursor: 'pointer',
                    color: 'var(--text-main)',
                    fontWeight: 600,
                    fontSize: '15px'
                  }}
                >
                  {faq.q}
                  {isOpen ? <ChevronUp size={20} color="var(--primary-color)" /> : <ChevronDown size={20} color="var(--text-muted)" />}
                </button>
                
                {isOpen && (
                  <div style={{ padding: '0 20px 20px', color: 'var(--text-muted)', fontSize: '14px', lineHeight: '1.6' }}>
                    {faq.a}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

    </div>
  );
};

export default Instructions;
